import React from 'react';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { classnames } from 'Utils/classnames/classnames.utils';
import { clearAllCacheFront } from 'Utils/graphql-client/cacheFront';
import * as pleUtils from 'Utils/ple-utils';
import { Button } from 'Components/frameworks/button/Button';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Title } from 'Components/frameworks/title/Title';
type Props = {
  hasFacebookConnect: boolean;
  hasQuickLogin: boolean;
  hasWechatConnect: boolean;
};
export const MemberSignIn = ({ hasFacebookConnect, hasQuickLogin, hasWechatConnect }: Props) => {
  const { locale } = useAppContext();
  const { trackClickEvent } = useTrackEvent();
  return (
    <div
      className={classnames('flex flex-col-reverse lg:flex-row', {
        'lg:min-w-320': !hasQuickLogin,
        'lg:min-w-[580px]': hasQuickLogin,
      })}
      data-testid="signInContent"
    >
      <div>
        <Title
          className="mb-10"
          iconColor={COLORS.SIENNA}
          iconName={ICONS.ICON_45}
          iconSize="1.875rem"
          theme="title-brand-n3"
        >
          {t('account.authentication.yourAccount.title')}
        </Title>
        <p className="mb-20 text-12 leading-tight text-black">
          {t('account.authentication.yourAccount.text')}
        </p>

        <div className="mb-20 flex gap-10">
          <Button
            data-testid="member-nav-signIn"
            isExternalLink
            onClick={() => {
              clearAllCacheFront();
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
                detail_click: 'memberSpace_signIn',
              });
            }}
            size="large"
            url={pleUtils.buildSigninUrl({
              locale,
            })}
          >
            {t('account.authentication.signin')}
          </Button>

          {hasFacebookConnect && (
            <Button
              className="min-w-50 leading-0"
              color={COLORS.WHITE}
              colorBg={COLORS.ULTRAMARINE}
              colorBorder={COLORS.ULTRAMARINE}
              data-testid="MemberSignIn-facebook"
              isExternalLink
              isSquare
              onClick={() => {
                clearAllCacheFront();
                trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
                  detail_click: 'memberSpace_incentive_FB_connect',
                });
              }}
              size="large"
              url={pleUtils.buildFacebookConnectUrl({
                locale,
              })}
            >
              <Icon name={ICONS.FACEBOOK} width="1.5rem" />
              <span className="sr-only">Club Med - Facebook</span>
            </Button>
          )}
          {hasWechatConnect && (
            <Button
              className="min-w-50 leading-0"
              color={COLORS.WHITE}
              colorBg={COLORS.ULTRAMARINE}
              colorBorder={COLORS.ULTRAMARINE}
              data-testid="MemberSignIn-wechat"
              isExternalLink
              isSquare
              onClick={() =>
                trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
                  detail_click: 'memberSpace_incentive_wechat_connect',
                })
              }
              size="large"
              url={pleUtils.buildWechatConnectUrl({
                locale,
              })}
            >
              <Icon name={ICONS.WECHAT} width="1.5rem" />
              <span className="sr-only">Club Med - Wechat</span>
            </Button>
          )}
        </div>

        <div className="flex gap-10 font-sans text-12 leading-tight text-black">
          <p>{t('account.authentication.newToClubMed')}</p>
          <a
            className="font-bold text-sienna underline hocus:text-sienna-active"
            data-testid="member-nav-createAccount"
            href={pleUtils.buildSignupUrl({
              locale,
            })}
            onClick={() =>
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
                detail_click: 'memberSpace_createAccount',
              })
            }
            rel="nofollow"
          >
            {t('account.authentication.createAccountLinkLabel')}
          </a>
        </div>
      </div>

      {hasQuickLogin && (
        <div className="mb-20 border-b-1 border-sand pb-20 lg:mb-0 lg:ms-20 lg:border-0 lg:border-s-1 lg:pb-0 lg:ps-20">
          <Title
            className="mb-10"
            iconColor={COLORS.SIENNA}
            iconName={ICONS.DOCUMENT_COLORED}
            iconSize="1.875rem"
            theme="title-brand-n3"
          >
            {t('account.authentication.quickLogin.title')}
          </Title>
          <p className="mb-20 text-12 leading-tight text-black">
            {t('account.authentication.quickLogin.text')}
          </p>
          <ul className="mb-20 text-12 leading-tight">
            {Array(10)
              .fill()
              .map((_, i) => t(`account.authentication.quickLogin.keyPoint.${i}`, {}, ''))
              .filter(Boolean)
              .map((key) => (
                <li key={key} className="mb-10 flex items-start gap-10">
                  <Icon className="shrink-0 text-green" name={ICONS.CHECK} width="1rem" />
                  {key}
                </li>
              ))}
          </ul>
          <Button
            className="w-full"
            color={COLORS.BLACK}
            colorBg={COLORS.SAFFRON}
            colorBorder={COLORS.SAFFRON}
            data-testid="member-nav-quickLogin"
            isExternalLink
            onClick={() => {
              clearAllCacheFront();
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
                detail_click: 'memberSpace_quickLogin',
              });
            }}
            size="large"
            url={pleUtils.buildSigninUrl({
              provider: 'bookingGM',
              locale,
            })}
          >
            {t('account.authentication.quickLogin.cta')}
          </Button>
        </div>
      )}
    </div>
  );
};
